import React, { useRef, useState } from 'react';
import { Send, Rocket, RefreshCw, ExternalLink, Paperclip, Mic, MicOff, X, Zap, Github, GitFork } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const ChatInput = ({ input, setInput, onSend, onPublish, isStreaming, publishing, publishUrl, connected, hasMessages }) => {
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [ultraMode, setUltraMode] = useState(false);
  const mediaRecorderRef = useRef(null);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    let msg = input.trim();
    if (attachedFiles.length > 0) {
      const fileNames = attachedFiles.map(f => f.name).join(', ');
      msg = msg ? `${msg}\n\n[Attached: ${fileNames}]` : `[Attached: ${fileNames}]`;
      setAttachedFiles([]);
    }
    if (ultraMode && msg) {
      msg = `[ULTRA] ${msg}`;
    }
    if (msg) {
      setInput(msg);
      setTimeout(() => onSend(), 50);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024);
    if (validFiles.length < files.length) toast({ title: 'File too large', description: 'Max 10MB', variant: 'destructive' });
    setAttachedFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const toggleRecording = async () => {
    if (isRecording) { mediaRecorderRef.current?.stop(); setIsRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const file = new File([new Blob(chunks, { type: 'audio/webm' })], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        setAttachedFiles(prev => [...prev, file]);
        toast({ title: 'Voice recorded' });
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch { toast({ title: 'Microphone access denied', variant: 'destructive' }); }
  };

  const handleSaveGithub = () => toast({ title: 'Save to GitHub', description: 'Use the "Save to Github" feature in the sidebar to push your code.' });
  const handleFork = () => toast({ title: 'Project Forked', description: 'A copy of this project has been created.' });

  return (
    <div className="px-4 sm:px-8 pb-4 pt-2 bg-background">
      {publishUrl && (
        <div className="mb-3 flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl max-w-4xl mx-auto" data-testid="publish-success-banner">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">Published!</span>
          <a href={publishUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 underline truncate flex items-center gap-1">
            {publishUrl} <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Attached files */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="attached-files">
            {attachedFiles.map((file, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted rounded-lg border border-border text-xs">
                <Paperclip className="w-3 h-3 text-muted-foreground" />
                <span className="text-foreground max-w-[120px] truncate">{file.name}</span>
                <button onClick={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`remove-file-${i}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Big pill input — Emergent style */}
        <div className="relative bg-card border border-border rounded-3xl shadow-sm focus-within:border-primary/30 focus-within:shadow-md transition-all">
          {/* Top row — action buttons */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <div className="flex items-center gap-1">
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.json,.csv,.js,.jsx,.ts,.tsx,.py,.html,.css,.md" className="hidden" onChange={handleFileSelect} data-testid="file-input" />
              <Button type="button" onClick={() => fileInputRef.current?.click()} size="sm" variant="ghost" className="h-8 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg gap-1.5 text-xs" title="Attach file" data-testid="attach-file-btn">
                <Paperclip className="w-3.5 h-3.5" /> Attach
              </Button>
              <Button type="button" onClick={toggleRecording} size="sm" variant="ghost" className={`h-8 px-2.5 rounded-lg gap-1.5 text-xs ${isRecording ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`} title={isRecording ? 'Stop' : 'Voice'} data-testid="mic-btn">
                {isRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                {isRecording ? 'Stop' : 'Voice'}
              </Button>
              <div className="w-px h-5 bg-border mx-1" />
              <Button type="button" onClick={() => setUltraMode(!ultraMode)} size="sm" variant="ghost" className={`h-8 px-2.5 rounded-lg gap-1.5 text-xs ${ultraMode ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`} title="Ultra Thinking" data-testid="ultra-btn">
                <Zap className="w-3.5 h-3.5" /> Ultra {ultraMode && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1 py-0 h-4">ON</Badge>}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button type="button" onClick={handleFork} size="sm" variant="ghost" className="h-8 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg gap-1.5 text-xs" title="Fork project" data-testid="fork-btn">
                <GitFork className="w-3.5 h-3.5" /> Fork
              </Button>
              <Button type="button" onClick={handleSaveGithub} size="sm" variant="ghost" className="h-8 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg gap-1.5 text-xs" title="Save to GitHub" data-testid="save-github-btn">
                <Github className="w-3.5 h-3.5" /> Save to GitHub
              </Button>
            </div>
          </div>

          {/* Textarea row */}
          <div className="flex items-end px-3">
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe your app or ask for changes..."
              className="flex-1 resize-none min-h-[56px] max-h-[200px] border-0 bg-transparent text-foreground text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-2"
              rows={2}
            />
          </div>

          {/* Bottom row — publish + send */}
          <div className="flex items-center justify-between px-4 pb-3 pt-1">
            <p className="text-xs text-muted-foreground">
              {isRecording && <span className="text-red-500 animate-pulse mr-2">Recording...</span>}
              {attachedFiles.length > 0 && <span className="text-primary mr-2">{attachedFiles.length} file{attachedFiles.length > 1 ? 's' : ''}</span>}
              Enter to send &middot; Shift+Enter for new line
              {connected && <span className="text-emerald-500 ml-1">&middot; Live</span>}
            </p>
            <div className="flex items-center gap-2">
              <Button data-testid="publish-to-web-btn" onClick={onPublish} disabled={publishing || !hasMessages} size="sm" variant="outline" className="h-9 px-3 rounded-full gap-1.5 text-xs border-border disabled:opacity-20">
                {publishing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                Publish
              </Button>
              <Button data-testid="chat-send-btn" onClick={handleSend} disabled={(!input.trim() && attachedFiles.length === 0) || isStreaming} size="sm" className="h-9 px-5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full gap-1.5 text-xs font-medium disabled:opacity-20">
                <Send className="w-3.5 h-3.5" /> Send
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
